/**
 * 学员端统一请求层
 * -----------------------------------------------------------------------------
 * 三条纪律集中在这一个文件里，页面不各写一套：
 *   ① **Cookie 会话**：`credentials: 'include'`，会话标识是 `sid` Cookie，
 *      不接受任何「用户 id」参数（否则越权只是改一个参数）。
 *   ② **`x-entry` 头**：入口码随每次请求带上，服务端据此做入口级配置与审计。
 *   ③ **`idempotency-key` 头**：提交 / 创建等写动作重试时复用同一令牌，
 *      网络抖动不会变成两笔报名单。
 *
 * 出参统一是 `{ success: true, data }` / `{ success: false, code, message, details }`
 * （`packages/shared/src/errors.ts`），本层负责拆包并把失败码收窄成 `ApiError`。
 */
import type { AnswerError, ApiError as ApiErrorEnvelope } from '@enroll/shared';

/** 入口码请求头名（与服务端 `x-entry` 对齐） */
export const ENTRY_HEADER = 'x-entry';
/** 幂等令牌请求头名 */
export const IDEMPOTENCY_HEADER = 'idempotency-key';

/** 会话失效类错误码：命中即应回到引导页重新授权 */
export const SESSION_ERROR_CODES: readonly string[] = [
  'AUTH-001',
  'AUTH-002',
  'AUTH-003',
  'AUTH-004',
  'AUTH-010',
];

export interface ApiErrorDetails extends Record<string, unknown> {
  fields?: AnswerError[];
  missingSteps?: string[];
  field?: string;
  step?: string;
  score?: number;
  failCode?: string | null;
  failReason?: string | null;
}

/** 请求失败（含业务错误码与 details） */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: ApiErrorDetails;

  constructor(code: string, message: string, status: number, details?: ApiErrorDetails) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** 会话类错误：调用方据此跳回引导页 */
  get isSessionError(): boolean {
    return SESSION_ERROR_CODES.includes(this.code) || this.status === 401;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON 请求体（与 `formData` 互斥） */
  body?: unknown;
  /** multipart 请求体（核验影像上传用） */
  formData?: FormData;
  /** 入口码；缺省时由 `resolveEntryCode()` 从会话存储取 */
  entryCode?: string | null;
  /** 幂等令牌 */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

const JSON_HEADERS: Record<string, string> = { Accept: 'application/json' };

/** HTTP 状态码兜底文案（服务端未给 message 时使用） */
function fallbackByStatus(status: number): string {
  if (status === 401) return '登录状态已过期，请重新进入';
  if (status === 403) return '您没有权限执行此操作';
  if (status === 404) return '数据不存在或已被删除';
  if (status === 409) return '当前状态不允许该操作';
  if (status === 422) return '信息未达到要求，请按提示修改';
  if (status >= 500) return '系统繁忙，请稍后重试';
  return '请求失败，请稍后重试';
}

function isOkEnvelope(payload: unknown): payload is { success: true; data: unknown } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { success?: unknown }).success === true &&
    'data' in payload
  );
}

function isErrorEnvelope(payload: unknown): payload is ApiErrorEnvelope {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { success?: unknown }).success === false
  );
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // 非 JSON 响应（网关 HTML 错误页等）：原样作字符串，交给上层兜底文案
    return text;
  }
}

/**
 * 发一次请求并拆包。
 * 网络层异常（断网 / 被拦截）统一转成 `ApiError`，避免页面里出现两种错误形态。
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...JSON_HEADERS };
  const entryCode = options.entryCode ?? resolveEntryCode();
  if (entryCode) headers[ENTRY_HEADER] = entryCode;
  if (options.idempotencyKey) headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body,
      credentials: 'include',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('NETWORK-000', '网络连接失败，请检查网络后重试', 0);
  }

  const payload = await parseBody(response);

  if (response.ok && isOkEnvelope(payload)) return payload.data as T;

  if (isErrorEnvelope(payload)) {
    throw new ApiError(
      payload.code,
      payload.message || fallbackByStatus(response.status),
      response.status,
      payload.details as ApiErrorDetails | undefined,
    );
  }

  throw new ApiError(`HTTP-${response.status}`, fallbackByStatus(response.status), response.status);
}

// ---------------------------------------------------------------- 入口码

const ENTRY_STORAGE_KEY = 'enroll.entryCode';

/** 本地保存入口码（服务台首页 / 落地链接解析后调用） */
export function rememberEntryCode(code: string | null): void {
  try {
    if (code) window.sessionStorage.setItem(ENTRY_STORAGE_KEY, code);
    else window.sessionStorage.removeItem(ENTRY_STORAGE_KEY);
  } catch {
    // 隐私模式下 sessionStorage 可能不可写；入口码缺失只会少一次归因，不影响主链路
  }
}

/** 读取入口码；无则返回 null（不抛错，调用方无须处理存储异常） */
export function resolveEntryCode(): string | null {
  try {
    return window.sessionStorage.getItem(ENTRY_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** 从 URL 查询串解析入口码（`?entry=xxx`），并落地到会话存储 */
export function consumeEntryFromUrl(search: string): string | null {
  const params = new URLSearchParams(search);
  const entry = params.get('entry');
  if (entry) rememberEntryCode(entry);
  return entry;
}

// ---------------------------------------------------------------- 幂等令牌

/** 生成一次性幂等令牌（优先 crypto.randomUUID，降级为时间戳 + 随机串） */
export function newIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID;
  if (typeof uuid === 'function') return uuid.call(globalThis.crypto);
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
